import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useHistory, useParams } from 'react-router-dom';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import { notification } from '@spark/ui-components/utils/notification';
import {
  createTagOuvidoria,
  getTagOuvidoriaById,
  updateTagOuvidoria,
} from '@spark/services';
import { formSections, formStructure, validateForm } from './formConfig';

const PRIORIDADE_OPTIONS = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Nao' },
];

const findPrioridadeOption = value => PRIORIDADE_OPTIONS.find(item => item.value === String(Boolean(value))) || PRIORIDADE_OPTIONS[1];

const normalizePrioridade = value => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  if (value && typeof value === 'object' && 'value' in value) {
    return String(value.value).toLowerCase() === 'true';
  }

  return false;
};

const Forms = ({ isReadOnly = false }) => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [initialData, setInitialData] = useState(null);

  const isEditMode = !!id && id !== 'criar' && !isReadOnly;

  useEffect(() => {
    if (!(isReadOnly || isEditMode) || !id || id === 'criar') {
      return;
    }

    const load = async () => {
      try {
        setLoadingData(true);
        const data = await getTagOuvidoriaById(id);
        setInitialData({
          id: data?.id,
          nomeTag: data?.nomeTag || '',
          descricao: data?.descricao || '',
          dataCriacao: data?.dataCriacao || '',
          prioridade: findPrioridadeOption(data?.prioridade),
        });
      } catch (error) {
        console.error('Erro ao carregar tag da ouvidoria:', error);
        notification.error('Nao foi possivel carregar a tag da ouvidoria.');
      } finally {
        setLoadingData(false);
      }
    };

    load();
  }, [id, isEditMode, isReadOnly]);

  const defaultValues = useMemo(
    () => ({
      nomeTag: '',
      descricao: '',
      dataCriacao: '',
      prioridade: PRIORIDADE_OPTIONS[1],
    }),
    []
  );

  const initialValues = initialData || defaultValues;

  const buildComparablePayload = useCallback(values => ({
    nomeTag: values?.nomeTag?.trim() || '',
    descricao: values?.descricao?.trim() || '',
    prioridade: normalizePrioridade(values?.prioridade),
  }), []);

  const hasChanges = useCallback(
    values => {
      if (!isEditMode || !initialData) {
        return true;
      }

      const current = buildComparablePayload(values);
      const original = buildComparablePayload(initialData);
      return JSON.stringify(current) !== JSON.stringify(original);
    },
    [buildComparablePayload, initialData, isEditMode]
  );

  const onSubmit = useCallback(
    async values => {
      if (isEditMode && !hasChanges(values)) {
        notification.warning('Nenhuma informacao alterada');
        return;
      }

      try {
        setLoading(true);

        const payload = {
          id: initialData?.id || 0,
          nomeTag: values.nomeTag?.trim() || '',
          descricao: values.descricao?.trim() || '',
          dataCriacao: initialData?.dataCriacao || new Date().toISOString(),
          prioridade: normalizePrioridade(values.prioridade),
        };

        if (isEditMode) {
          await updateTagOuvidoria(id, payload);
          notification.success('Tag da ouvidoria atualizada com sucesso!');
        } else {
          await createTagOuvidoria(payload);
          notification.success('Tag da ouvidoria criada com sucesso!');
        }

        history.push('/pages/tagsouvidoria');
      } catch (error) {
        console.error('Erro ao salvar tag da ouvidoria:', error);
        notification.error('Erro ao salvar a tag da ouvidoria. Tente novamente.');
      } finally {
        setLoading(false);
      }
    },
    [hasChanges, history, id, initialData?.dataCriacao, initialData?.id, isEditMode]
  );

  if ((isReadOnly || isEditMode) && loadingData) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: 'center', padding: 50 }}>
            Carregando dados da tag da ouvidoria...
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Form onSubmit={onSubmit} initialValues={initialValues} validate={validateForm}>
      {({ handleSubmit }) => (
        <Card>
          <CardBody>
            <FormContainer onSubmit={handleSubmit}>
              <DynamicForm
                structure={formStructure}
                sections={formSections(initialValues)}
                isViewMode={isReadOnly}
                isEditing={isEditMode}
              />

              {!isReadOnly && (
                <FormButtonToolbar className="mt-5">
                  <ButtonWithLoading
                    variant="primary"
                    type="submit"
                    loading={loading ? 'true' : undefined}
                    disabled={loading}
                  >
                    {isEditMode ? 'Atualizar' : 'Salvar'}
                  </ButtonWithLoading>

                  <Button variant="secondary" type="button" onClick={() => history.goBack()}>
                    Cancelar
                  </Button>
                </FormButtonToolbar>
              )}
            </FormContainer>
          </CardBody>
        </Card>
      )}
    </Form>
  );
};

export default Forms;
